import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageHeader } from '../components/common';
import { ROUTES } from '../constants';
import { ADMIN_ROLE_CODES, ADMIN_ROLE_NAMES } from '../constants/userRoles';
import { adminUserService, ApiError } from '../services';
import { useAuth } from '../contexts/AuthContext';

const roleBadgeStyles = {
  SA: {
    bg: 'bg-amber-50 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    icon: (
      <svg className='w-3.5 h-3.5 mr-1 text-amber-600' fill='currentColor' viewBox='0 0 20 20'>
        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
      </svg>
    ),
  },
  A: {
    bg: 'bg-purple-50 text-purple-800 border-purple-200',
    dot: 'bg-purple-500',
    icon: (
      <svg className='w-3.5 h-3.5 mr-1 text-purple-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
      </svg>
    ),
  },
  P: {
    bg: 'bg-blue-50 text-blue-800 border-blue-200',
    dot: 'bg-blue-500',
    icon: null,
  },
  M: {
    bg: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    dot: 'bg-emerald-500',
    icon: null,
  },
  S: {
    bg: 'bg-gray-50 text-gray-800 border-gray-200',
    dot: 'bg-gray-500',
    icon: null,
  },
};

const UsersPage = () => {
  const { user } = useAuth();

  // Determine current user's privilege level: Super Admin (SA) has full access, Admin (A) is read-only
  const isSuperAdmin = Boolean(
    user?.roles?.admin?.some(r =>
      ['Super Administrator', 'SA', 'super_admin'].includes(r)
    ) ||
    user?.role === 'super_admin' ||
    user?.role === 'SA'
  );

  // Users listing state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
    count: 0,
    pageCount: 1,
    total: 0,
  });

  // Filters
  const [searchKeyword, setSearchKeyword] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeAddTab, setActiveAddTab] = useState('existing'); // 'existing' | 'invite'
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form states for Add Existing
  const [existingSearchKeyword, setExistingSearchKeyword] = useState('');
  const [existingSearchResults, setExistingSearchResults] = useState([]);
  const [searchingAccounts, setSearchingAccounts] = useState(false);
  const [selectedExistingAccount, setSelectedExistingAccount] = useState(null);
  const [existingRole, setExistingRole] = useState(ADMIN_ROLE_CODES.ADMIN);
  const [submittingExisting, setSubmittingExisting] = useState(false);

  // Form states for Invite New
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: ADMIN_ROLE_CODES.ADMIN,
  });
  const [submittingInvite, setSubmittingInvite] = useState(false);

  // Form states for Edit
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    active: true,
  });
  const [submittingEdit, setSubmittingEdit] = useState(false);

  // Deleting state
  const [deletingUser, setDeletingUser] = useState(false);

  // Debounce timer refs
  const searchDebounceRef = useRef(null);
  const accountSearchDebounceRef = useRef(null);

  // Fetch admin users list
  const fetchUsers = useCallback(
    async (params = {}) => {
      setLoading(true);
      setError(null);
      try {
        const queryParams = {
          page: params.page || pagination.page,
          size: params.size || pagination.size,
          keyword: params.keyword !== undefined ? params.keyword : searchKeyword,
          role: params.role !== undefined ? params.role : roleFilter,
        };

        const activeVal = params.active !== undefined ? params.active : statusFilter;
        if (activeVal === 'true') queryParams.active = true;
        if (activeVal === 'false') queryParams.active = false;

        const response = await adminUserService.getUsers(queryParams);
        if (response && response.data) {
          setUsers(response.data.data || []);
          setPagination({
            page: response.data.page || 1,
            size: response.data.size || 10,
            count: response.data.count || 0,
            pageCount: response.data.pageCount || 1,
            total: response.data.total || 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch admin users:', err);
        setError(err.message || 'Failed to load admin users.');
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, pagination.size, searchKeyword, roleFilter, statusFilter]
  );

  // Initial load
  useEffect(() => {
    fetchUsers({ page: 1 });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search filter with debounce
  const handleSearchChange = e => {
    const val = e.target.value;
    setSearchKeyword(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchUsers({ keyword: val, page: 1 });
    }, 400);
  };

  // Handle role filter change
  const handleRoleFilterChange = e => {
    const val = e.target.value;
    setRoleFilter(val);
    fetchUsers({ role: val, page: 1 });
  };

  // Handle status filter change
  const handleStatusFilterChange = e => {
    const val = e.target.value;
    setStatusFilter(val);
    fetchUsers({ active: val, page: 1 });
  };

  // Search existing accounts for Add Existing Tab
  const handleAccountSearchChange = e => {
    const query = e.target.value;
    setExistingSearchKeyword(query);
    setSelectedExistingAccount(null);

    if (accountSearchDebounceRef.current) clearTimeout(accountSearchDebounceRef.current);
    if (!query.trim()) {
      setExistingSearchResults([]);
      return;
    }

    accountSearchDebounceRef.current = setTimeout(async () => {
      setSearchingAccounts(true);
      try {
        const res = await adminUserService.searchAccounts(query.trim());
        setExistingSearchResults(res?.data || []);
      } catch (err) {
        console.error('Account search error:', err);
      } finally {
        setSearchingAccounts(false);
      }
    }, 350);
  };

  // Submit Add Existing Account
  const handleAddExistingSubmit = async e => {
    e.preventDefault();
    if (!selectedExistingAccount) return;

    setSubmittingExisting(true);
    try {
      await adminUserService.addExistingUser({
        accountId: selectedExistingAccount.id,
        role: existingRole,
      });

      setFeedback({
        type: 'success',
        message: `Successfully granted ${ADMIN_ROLE_NAMES[existingRole]} access to ${selectedExistingAccount.name || selectedExistingAccount.email}.`,
      });
      setShowAddModal(false);
      setSelectedExistingAccount(null);
      setExistingSearchKeyword('');
      setExistingSearchResults([]);
      fetchUsers({ page: 1 });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.message || 'Failed to add existing account as admin.',
      });
    } finally {
      setSubmittingExisting(false);
    }
  };

  // Submit Invite New Account
  const handleInviteSubmit = async e => {
    e.preventDefault();
    if (!inviteForm.name || !inviteForm.email) return;

    setSubmittingInvite(true);
    try {
      await adminUserService.inviteUser({
        name: inviteForm.name,
        email: inviteForm.email,
        phone: inviteForm.phone,
        role: inviteForm.role,
      });

      setFeedback({
        type: 'success',
        message: `Invitation dispatched to ${inviteForm.email} with temporary password and ${ADMIN_ROLE_NAMES[inviteForm.role]} privileges.`,
      });
      setShowAddModal(false);
      setInviteForm({
        name: '',
        email: '',
        phone: '',
        role: ADMIN_ROLE_CODES.ADMIN,
      });
      fetchUsers({ page: 1 });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.message || 'Failed to dispatch admin invitation.',
      });
    } finally {
      setSubmittingInvite(false);
    }
  };

  // Open Edit Modal
  const openEditModal = adminUser => {
    setSelectedUser(adminUser);
    setEditForm({
      name: adminUser.name || '',
      email: adminUser.email || '',
      phone: adminUser.phone || '',
      role: adminUser.role || ADMIN_ROLE_CODES.ADMIN,
      active: adminUser.active,
    });
    setShowEditModal(true);
  };

  // Submit Edit
  const handleEditSubmit = async e => {
    e.preventDefault();
    if (!selectedUser) return;

    setSubmittingEdit(true);
    try {
      await adminUserService.updateUser(selectedUser.id, editForm);

      setFeedback({
        type: 'success',
        message: `Admin user ${editForm.name || selectedUser.name} successfully updated and synced across accounts.`,
      });
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.message || 'Failed to update admin user.',
      });
    } finally {
      setSubmittingEdit(false);
    }
  };

  // Toggle active status directly from table
  const handleToggleActive = async adminUser => {
    if (!isSuperAdmin) return;
    try {
      await adminUserService.updateUser(adminUser.id, {
        active: !adminUser.active,
      });

      setFeedback({
        type: 'success',
        message: `Admin user status changed to ${!adminUser.active ? 'Active' : 'Inactive'}.`,
      });
      fetchUsers();
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.message || 'Failed to toggle admin status.',
      });
    }
  };

  // Open Delete Modal
  const openDeleteModal = adminUser => {
    setSelectedUser(adminUser);
    setShowDeleteModal(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    setDeletingUser(true);
    try {
      await adminUserService.deleteUser(selectedUser.id);
      setFeedback({
        type: 'success',
        message: `Admin privileges for ${selectedUser.name || selectedUser.email} have been revoked.`,
      });
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.message || 'Failed to remove admin user.',
      });
    } finally {
      setDeletingUser(false);
    }
  };

  // Dismiss feedback
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 7000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  // Compute metrics
  const superAdminCount = users.filter(u => u.role === 'SA').length;
  const activeCount = users.filter(u => u.active).length;
  const inactiveCount = users.filter(u => !u.active).length;

  return (
    <div className='min-h-screen pb-12'>
      <PageHeader
        title='Users Management'
        subtitle='Manage portal administrator accounts, role permissions, and user invitations'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Users' },
        ]}
        action={
          <div className='flex items-center gap-3'>
            {isSuperAdmin ? (
              <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300 shadow-sm'>
                <svg className='w-3.5 h-3.5 mr-1.5 text-amber-600' fill='currentColor' viewBox='0 0 20 20'>
                  <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                </svg>
                Super Admin Access
              </span>
            ) : (
              <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-900 border border-purple-200 shadow-sm'>
                <svg className='w-3.5 h-3.5 mr-1.5 text-purple-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                </svg>
                Read-Only Access
              </span>
            )}

            {isSuperAdmin && (
              <button
                type='button'
                onClick={() => {
                  setShowAddModal(true);
                  setActiveAddTab('existing');
                  setSelectedExistingAccount(null);
                  setExistingSearchKeyword('');
                  setExistingSearchResults([]);
                }}
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150'
              >
                <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
                </svg>
                Add Admin User
              </button>
            )}
          </div>
        }
      />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Feedback Alert Banner */}
        {feedback && (
          <div
            className={`mb-6 p-4 rounded-xl border flex items-center justify-between shadow-sm transition-all duration-300 ${
              feedback.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <div className='flex items-center space-x-3'>
              {feedback.type === 'success' ? (
                <svg className='w-5 h-5 text-emerald-600' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                </svg>
              ) : (
                <svg className='w-5 h-5 text-red-600' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
                </svg>
              )}
              <span className='text-sm font-medium'>{feedback.message}</span>
            </div>
            <button
              type='button'
              onClick={() => setFeedback(null)}
              className='text-gray-400 hover:text-gray-600 p-1'
            >
              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
          </div>
        )}

        {/* Informative Banner for Read-Only Admins */}
        {!isSuperAdmin && (
          <div className='mb-6 p-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 flex items-start space-x-3'>
            <svg className='w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
            <div className='text-sm'>
              <p className='font-semibold'>Read-Only Administrator Mode</p>
              <p className='text-blue-700 mt-0.5'>
                You have read-only privileges. You can view the admin team list and profile details, but inviting, editing, or deleting admin users requires <strong>Super Administrator</strong> access.
              </p>
            </div>
          </div>
        )}

        {/* Metrics Summary Cards */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8'>
          <div className='bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between'>
            <div>
              <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Total Admins</p>
              <p className='text-2xl font-bold text-gray-900 mt-1'>{pagination.total || users.length}</p>
            </div>
            <div className='w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600'>
              <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' />
              </svg>
            </div>
          </div>

          <div className='bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between'>
            <div>
              <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Super Admins (SA)</p>
              <p className='text-2xl font-bold text-amber-600 mt-1'>{superAdminCount}</p>
            </div>
            <div className='w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600'>
              <svg className='w-6 h-6' fill='currentColor' viewBox='0 0 20 20'>
                <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
              </svg>
            </div>
          </div>

          <div className='bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between'>
            <div>
              <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Active Users</p>
              <p className='text-2xl font-bold text-emerald-600 mt-1'>{activeCount}</p>
            </div>
            <div className='w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600'>
              <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
            </div>
          </div>

          <div className='bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between'>
            <div>
              <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Inactive / Suspended</p>
              <p className='text-2xl font-bold text-gray-600 mt-1'>{inactiveCount}</p>
            </div>
            <div className='w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-gray-500'>
              <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' />
              </svg>
            </div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className='bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6'>
          <div className='flex flex-col md:flex-row items-center gap-4'>
            {/* Search */}
            <div className='relative flex-1 w-full'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <svg className='h-5 w-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                </svg>
              </div>
              <input
                type='text'
                value={searchKeyword}
                onChange={handleSearchChange}
                placeholder='Search admin users by name, email, or phone...'
                className='block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              />
              {searchKeyword && (
                <button
                  type='button'
                  onClick={() => {
                    setSearchKeyword('');
                    fetchUsers({ keyword: '', page: 1 });
                  }}
                  className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600'
                >
                  <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              )}
            </div>

            {/* Role Filter */}
            <div className='w-full md:w-56'>
              <select
                value={roleFilter}
                onChange={handleRoleFilterChange}
                className='block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              >
                <option value=''>All Roles</option>
                <option value={ADMIN_ROLE_CODES.SUPER_ADMIN}>Super Administrator (SA)</option>
                <option value={ADMIN_ROLE_CODES.ADMIN}>Administrator (A)</option>
                <option value={ADMIN_ROLE_CODES.OPERATION}>Operation (P)</option>
                <option value={ADMIN_ROLE_CODES.MARKETING}>Marketing (M)</option>
                <option value={ADMIN_ROLE_CODES.STAFF}>Staff (S)</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className='w-full md:w-44'>
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className='block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              >
                <option value=''>All Status</option>
                <option value='true'>Active</option>
                <option value='false'>Inactive</option>
              </select>
            </div>

            {/* Refresh Button */}
            <button
              type='button'
              onClick={() => fetchUsers()}
              disabled={loading}
              className='p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500'
              title='Refresh list'
            >
              <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
              </svg>
            </button>
          </div>
        </div>

        {/* Users Table Card */}
        <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
          {loading && users.length === 0 ? (
            <div className='p-12 text-center'>
              <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
              <p className='mt-3 text-sm text-gray-500'>Loading admin users directory...</p>
            </div>
          ) : error ? (
            <div className='p-12 text-center'>
              <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 text-red-600'>
                <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' />
                </svg>
              </div>
              <h3 className='mt-2 text-sm font-medium text-gray-900'>Error loading users</h3>
              <p className='mt-1 text-sm text-gray-500'>{error}</p>
              <div className='mt-6'>
                <button
                  type='button'
                  onClick={() => fetchUsers()}
                  className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700'
                >
                  Retry
                </button>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className='p-12 text-center'>
              <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 text-gray-400'>
                <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z' />
                </svg>
              </div>
              <h3 className='mt-2 text-sm font-medium text-gray-900'>No admin users found</h3>
              <p className='mt-1 text-sm text-gray-500'>
                {searchKeyword || roleFilter || statusFilter
                  ? 'Try adjusting your search terms or filters.'
                  : 'Get started by inviting or adding a new administrator.'}
              </p>
              {isSuperAdmin && (
                <div className='mt-6'>
                  <button
                    type='button'
                    onClick={() => setShowAddModal(true)}
                    className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700'
                  >
                    Add Admin User
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200 text-left text-sm'>
                <thead className='bg-gray-50 font-medium text-gray-600'>
                  <tr>
                    <th scope='col' className='px-6 py-3.5'>Admin User</th>
                    <th scope='col' className='px-6 py-3.5'>Role</th>
                    <th scope='col' className='px-6 py-3.5'>Status</th>
                    <th scope='col' className='px-6 py-3.5'>Added On</th>
                    <th scope='col' className='px-6 py-3.5 text-right'>Actions</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200 bg-white'>
                  {users.map(u => {
                    const badge = roleBadgeStyles[u.role] || roleBadgeStyles.S;
                    const initials = (u.name || u.email || 'A')
                      .split(' ')
                      .map(p => p[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <tr key={u.id} className='hover:bg-gray-50 transition-colors'>
                        {/* User column */}
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='flex items-center'>
                            <div className='flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white font-semibold shadow-sm'>
                              {initials}
                            </div>
                            <div className='ml-4'>
                              <div className='font-medium text-gray-900'>{u.name || 'Unnamed'}</div>
                              <div className='text-xs text-gray-500'>{u.email}</div>
                              {u.phone && <div className='text-xs text-gray-400 mt-0.5'>{u.phone}</div>}
                            </div>
                          </div>
                        </td>

                        {/* Role column */}
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.bg}`}
                          >
                            {badge.icon}
                            {u.roleName || ADMIN_ROLE_NAMES[u.role] || u.role}
                          </span>
                        </td>

                        {/* Status column */}
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='flex items-center space-x-2'>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                u.active
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${u.active ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                              {u.active ? 'Active' : 'Inactive'}
                            </span>
                            {isSuperAdmin && (
                              <button
                                type='button'
                                onClick={() => handleToggleActive(u)}
                                title={`Click to mark as ${u.active ? 'Inactive' : 'Active'}`}
                                className='text-gray-400 hover:text-gray-600 text-xs underline ml-1'
                              >
                                {u.active ? 'Deactivate' : 'Activate'}
                              </button>
                            )}
                          </div>
                        </td>

                        {/* Created At column */}
                        <td className='px-6 py-4 whitespace-nowrap text-xs text-gray-500'>
                          <div>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</div>
                          {u.createdByName && (
                            <div className='text-gray-400 mt-0.5'>by {u.createdByName}</div>
                          )}
                        </td>

                        {/* Actions column */}
                        <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                          {isSuperAdmin ? (
                            <div className='flex items-center justify-end space-x-2'>
                              <button
                                type='button'
                                onClick={() => openEditModal(u)}
                                className='inline-flex items-center px-2.5 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition'
                              >
                                <svg className='w-3.5 h-3.5 mr-1 text-gray-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                                </svg>
                                Edit
                              </button>
                              <button
                                type='button'
                                onClick={() => openDeleteModal(u)}
                                className='inline-flex items-center px-2.5 py-1.5 border border-red-200 rounded-md text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 transition'
                              >
                                <svg className='w-3.5 h-3.5 mr-1 text-red-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                                </svg>
                                Remove
                              </button>
                            </div>
                          ) : (
                            <button
                              type='button'
                              onClick={() => openEditModal(u)}
                              className='inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100'
                            >
                              View Details
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {users.length > 0 && (
            <div className='px-6 py-4 flex items-center justify-between border-t border-gray-200 bg-gray-50'>
              <div className='text-xs text-gray-600'>
                Showing <span className='font-semibold'>{(pagination.page - 1) * pagination.size + 1}</span> to{' '}
                <span className='font-semibold'>{Math.min(pagination.page * pagination.size, pagination.total || users.length)}</span> of{' '}
                <span className='font-semibold'>{pagination.total || users.length}</span> administrators
              </div>
              <div className='flex space-x-2'>
                <button
                  type='button'
                  onClick={() => fetchUsers({ page: pagination.page - 1 })}
                  disabled={pagination.page <= 1 || loading}
                  className='px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Previous
                </button>
                <span className='px-3 py-1.5 text-xs text-gray-600'>
                  Page {pagination.page} of {pagination.pageCount || 1}
                </span>
                <button
                  type='button'
                  onClick={() => fetchUsers({ page: pagination.page + 1 })}
                  disabled={pagination.page >= pagination.pageCount || loading}
                  className='px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* ADD ADMIN USER MODAL (Tab 1: Existing, Tab 2: Invite New)                 */}
      {/* ========================================================================= */}
      {showAddModal && (
        <div className='fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4'>
          <div className='bg-white rounded-2xl max-w-xl w-full shadow-2xl overflow-hidden transform transition-all'>
            {/* Modal Header */}
            <div className='px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50'>
              <div>
                <h3 className='text-lg font-bold text-gray-900'>Add Administrator</h3>
                <p className='text-xs text-gray-500 mt-0.5'>Assign admin roles to existing users or invite brand new admins</p>
              </div>
              <button
                type='button'
                onClick={() => setShowAddModal(false)}
                className='text-gray-400 hover:text-gray-600 p-1.5 rounded-lg'
              >
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>

            {/* Modal Tabs */}
            <div className='border-b border-gray-200 bg-white px-6'>
              <nav className='-mb-px flex space-x-8'>
                <button
                  type='button'
                  onClick={() => setActiveAddTab('existing')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeAddTab === 'existing'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Add Existing Account
                </button>
                <button
                  type='button'
                  onClick={() => setActiveAddTab('invite')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeAddTab === 'invite'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Invite New Account
                </button>
              </nav>
            </div>

            {/* Tab 1: Add Existing Account */}
            {activeAddTab === 'existing' && (
              <form onSubmit={handleAddExistingSubmit} className='p-6 space-y-5'>
                <div>
                  <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2'>
                    Find User Account
                  </label>
                  <div className='relative'>
                    <input
                      type='text'
                      value={existingSearchKeyword}
                      onChange={handleAccountSearchChange}
                      placeholder='Search by account email, phone, or name...'
                      className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    />
                    {searchingAccounts && (
                      <div className='absolute right-3 top-3'>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600'></div>
                      </div>
                    )}
                  </div>

                  {/* Search Results Dropdown / List */}
                  {existingSearchResults.length > 0 && !selectedExistingAccount && (
                    <div className='mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y shadow-md'>
                      {existingSearchResults.map(acc => (
                        <div
                          key={acc.id}
                          onClick={() => {
                            setSelectedExistingAccount(acc);
                            setExistingSearchResults([]);
                          }}
                          className={`p-3 cursor-pointer hover:bg-blue-50 transition flex items-center justify-between ${
                            acc.isAdmin ? 'opacity-60 bg-gray-50' : ''
                          }`}
                        >
                          <div>
                            <div className='font-medium text-sm text-gray-900'>{acc.name || 'Unnamed'}</div>
                            <div className='text-xs text-gray-500'>{acc.email} {acc.phone ? `• ${acc.phone}` : ''}</div>
                          </div>
                          <div>
                            {acc.isAdmin ? (
                              <span className='px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 font-medium'>
                                Already Admin ({acc.adminRole})
                              </span>
                            ) : (
                              <span className='text-xs font-semibold text-blue-600 hover:underline'>
                                Select
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Account Card */}
                {selectedExistingAccount && (
                  <div className='p-4 rounded-xl border border-blue-200 bg-blue-50 flex items-center justify-between'>
                    <div>
                      <div className='flex items-center space-x-2'>
                        <span className='font-bold text-gray-900 text-sm'>{selectedExistingAccount.name || 'Unnamed'}</span>
                        <span className='text-xs text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded font-medium'>Selected</span>
                      </div>
                      <div className='text-xs text-gray-600 mt-1'>{selectedExistingAccount.email}</div>
                      {selectedExistingAccount.phone && (
                        <div className='text-xs text-gray-500'>{selectedExistingAccount.phone}</div>
                      )}
                    </div>
                    <button
                      type='button'
                      onClick={() => setSelectedExistingAccount(null)}
                      className='text-xs text-gray-500 hover:text-red-600 font-medium'
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* Role selection */}
                <div>
                  <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2'>
                    Assign Admin Role
                  </label>
                  <select
                    value={existingRole}
                    onChange={e => setExistingRole(e.target.value)}
                    className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    <option value={ADMIN_ROLE_CODES.SUPER_ADMIN}>Super Administrator (SA) — Full CRUD Access</option>
                    <option value={ADMIN_ROLE_CODES.ADMIN}>Administrator (A) — Read-Only Access</option>
                    <option value={ADMIN_ROLE_CODES.OPERATION}>Operation (P) — Operational Access</option>
                    <option value={ADMIN_ROLE_CODES.MARKETING}>Marketing (M) — Marketing Access</option>
                    <option value={ADMIN_ROLE_CODES.STAFF}>Staff (S) — Standard Staff Access</option>
                  </select>
                </div>

                <div className='p-3.5 bg-gray-50 rounded-lg text-xs text-gray-600 border border-gray-200'>
                  <strong>Note:</strong> Adding an existing user grants administrative permissions linked to their account. They will continue using their current login password.
                </div>

                <div className='flex justify-end space-x-3 pt-3 border-t border-gray-200'>
                  <button
                    type='button'
                    onClick={() => setShowAddModal(false)}
                    className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    disabled={!selectedExistingAccount || submittingExisting}
                    className='px-5 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm'
                  >
                    {submittingExisting && (
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                    )}
                    Assign Admin Role
                  </button>
                </div>
              </form>
            )}

            {/* Tab 2: Invite New Account */}
            {activeAddTab === 'invite' && (
              <form onSubmit={handleInviteSubmit} className='p-6 space-y-4'>
                <div>
                  <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>
                    Full Name <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    required
                    value={inviteForm.name}
                    onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })}
                    placeholder='e.g. Marco Rossi'
                    className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500'
                  />
                </div>

                <div>
                  <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>
                    Email Address <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='email'
                    required
                    value={inviteForm.email}
                    onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder='admin@domain.com'
                    className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500'
                  />
                </div>

                <div>
                  <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>
                    Phone Number <span className='text-gray-400 font-normal'>(Optional)</span>
                  </label>
                  <input
                    type='tel'
                    value={inviteForm.phone}
                    onChange={e => setInviteForm({ ...inviteForm, phone: e.target.value })}
                    placeholder='+39 333 1234567'
                    className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500'
                  />
                </div>

                <div>
                  <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>
                    Assigned Role <span className='text-red-500'>*</span>
                  </label>
                  <select
                    value={inviteForm.role}
                    onChange={e => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500'
                  >
                    <option value={ADMIN_ROLE_CODES.SUPER_ADMIN}>Super Administrator (SA) — Full CRUD Access</option>
                    <option value={ADMIN_ROLE_CODES.ADMIN}>Administrator (A) — Read-Only Access</option>
                    <option value={ADMIN_ROLE_CODES.OPERATION}>Operation (P) — Operational Access</option>
                    <option value={ADMIN_ROLE_CODES.MARKETING}>Marketing (M) — Marketing Access</option>
                    <option value={ADMIN_ROLE_CODES.STAFF}>Staff (S) — Standard Staff Access</option>
                  </select>
                </div>

                <div className='p-3.5 rounded-xl bg-blue-50 border border-blue-200 text-xs text-blue-900'>
                  <strong>Temporary Password Email:</strong> A secure temporary password will be automatically generated and emailed to the address above, synchronized across both account tables.
                </div>

                <div className='flex justify-end space-x-3 pt-3 border-t border-gray-200'>
                  <button
                    type='button'
                    onClick={() => setShowAddModal(false)}
                    className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    disabled={submittingInvite || !inviteForm.name || !inviteForm.email}
                    className='px-5 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm'
                  >
                    {submittingInvite && (
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                    )}
                    Dispatch Invitation
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* EDIT / VIEW ADMIN USER MODAL                                              */}
      {/* ========================================================================= */}
      {showEditModal && selectedUser && (
        <div className='fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4'>
          <div className='bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden'>
            <div className='px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50'>
              <h3 className='text-lg font-bold text-gray-900'>
                {isSuperAdmin ? 'Edit Administrator' : 'Administrator Details'}
              </h3>
              <button
                type='button'
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                }}
                className='text-gray-400 hover:text-gray-600 p-1.5'
              >
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className='p-6 space-y-4'>
              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>
                  Full Name
                </label>
                <input
                  type='text'
                  disabled={!isSuperAdmin}
                  value={editForm.name}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-600'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>
                  Email Address
                </label>
                <input
                  type='email'
                  disabled={!isSuperAdmin}
                  value={editForm.email}
                  onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                  className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-600'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>
                  Phone Number
                </label>
                <input
                  type='tel'
                  disabled={!isSuperAdmin}
                  value={editForm.phone}
                  onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                  className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-600'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>
                  Role
                </label>
                <select
                  disabled={!isSuperAdmin}
                  value={editForm.role}
                  onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                  className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm bg-white disabled:bg-gray-100 disabled:text-gray-600'
                >
                  <option value={ADMIN_ROLE_CODES.SUPER_ADMIN}>Super Administrator (SA) — Full Access</option>
                  <option value={ADMIN_ROLE_CODES.ADMIN}>Administrator (A) — Read-Only</option>
                  <option value={ADMIN_ROLE_CODES.OPERATION}>Operation (P)</option>
                  <option value={ADMIN_ROLE_CODES.MARKETING}>Marketing (M)</option>
                  <option value={ADMIN_ROLE_CODES.STAFF}>Staff (S)</option>
                </select>
              </div>

              {isSuperAdmin && (
                <div className='flex items-center space-x-3 pt-2'>
                  <input
                    type='checkbox'
                    id='activeStatus'
                    checked={editForm.active}
                    onChange={e => setEditForm({ ...editForm, active: e.target.checked })}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                  />
                  <label htmlFor='activeStatus' className='text-sm text-gray-700 font-medium'>
                    Active Account Access
                  </label>
                </div>
              )}

              {isSuperAdmin && (
                <div className='p-3 bg-gray-50 rounded-lg text-xs text-gray-500'>
                  Changes to name, email, and phone will automatically synchronize across both <code>skaix_account</code> and <code>account</code> tables.
                </div>
              )}

              <div className='flex justify-end space-x-3 pt-3 border-t border-gray-200'>
                <button
                  type='button'
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                  className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
                >
                  {isSuperAdmin ? 'Cancel' : 'Close'}
                </button>
                {isSuperAdmin && (
                  <button
                    type='submit'
                    disabled={submittingEdit}
                    className='px-5 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center shadow-sm'
                  >
                    {submittingEdit && (
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                    )}
                    Save Changes
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DELETE / REVOKE CONFIRMATION MODAL                                        */}
      {/* ========================================================================= */}
      {showDeleteModal && selectedUser && (
        <div className='fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4'>
          <div className='bg-white rounded-2xl max-w-md w-full shadow-2xl p-6'>
            <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 text-red-600 mb-4'>
              <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' />
              </svg>
            </div>
            <h3 className='text-lg font-bold text-gray-900 text-center'>Revoke Admin Access</h3>
            <p className='text-sm text-gray-600 text-center mt-2'>
              Are you sure you want to remove administrator privileges for{' '}
              <strong className='text-gray-900'>{selectedUser.name || selectedUser.email}</strong>?
            </p>
            <p className='text-xs text-gray-500 text-center mt-2'>
              This will immediately revoke their access to the SKAi1 Admin Portal.
            </p>

            <div className='flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200'>
              <button
                type='button'
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleConfirmDelete}
                disabled={deletingUser}
                className='px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 flex items-center'
              >
                {deletingUser && (
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                )}
                Revoke Privileges
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
